import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { projectService, Project } from '../services/project.service';
import { advanceService } from '../services/advance.service';
import { offlineAdvanceService } from '../services/offline-advance.service';

interface AdvanceReportFormProps {
  initialProjectId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

function getCurrentWeekNumber(): number {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const diff = (now.getTime() - start.getTime()) / 86400000;
  return Math.ceil((diff + start.getDay() + 1) / 7);
}

function getWeekStart(): number {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(now.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  return monday.getTime();
}

function getWeekEnd(): number {
  const monday = new Date(getWeekStart());
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return sunday.getTime();
}

export function AdvanceReportForm({
  initialProjectId,
  onSuccess,
  onCancel,
}: AdvanceReportFormProps) {
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useToast();
  const { isOnline } = useNetworkStatus();
  const { refresh: refreshQueue } = useOfflineSync();

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    project_id: initialProjectId ?? '',
    week_number: getCurrentWeekNumber(),
    year: new Date().getFullYear(),
    progress_percentage: 0,
    description: '',
    contractor_specialty: '',
  });

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);

  useEffect(() => {
    projectService
      .getProjects()
      .then((data) =>
        setProjects(data.filter((p) => p.status === 'in_progress' || p.status === 'planning'))
      )
      .catch((e) => {
        console.error(e);
        showError('No se pudieron cargar los proyectos');
      })
      .finally(() => setLoading(false));
  }, [showError]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const arr = Array.from(files);
    setSelectedFiles((prev) => [...prev, ...arr]);
    arr.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPreviews((prev) => [...prev, ev.target?.result as string]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removePhoto = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const dataUrlsFromFiles = async (files: File[]): Promise<string[]> => {
    const urls: string[] = [];
    for (const file of files) {
      const compressed = await advanceService.compressImage(file).catch(() => file);
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target?.result as string);
        reader.onerror = () => reject(new Error('Error leyendo archivo'));
        reader.readAsDataURL(compressed);
      });
      urls.push(dataUrl);
    }
    return urls;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.project_id) {
      showError('Selecciona un proyecto');
      return;
    }
    if (formData.progress_percentage <= 0) {
      showError('El porcentaje debe ser mayor a 0');
      return;
    }
    setIsSaving(true);

    try {
      if (isOnline) {
        await advanceService.createAdvance({
          project_id: formData.project_id,
          week_number: formData.week_number,
          year: formData.year,
          week_start: getWeekStart(),
          week_end: getWeekEnd(),
          description: formData.description || undefined,
          progress_percentage: formData.progress_percentage,
          contractor_specialty: formData.contractor_specialty || undefined,
          photos: selectedFiles.length > 0 ? selectedFiles : undefined,
        });
        showSuccess('Avance registrado');
      } else {
        const photoDataUrls = await dataUrlsFromFiles(selectedFiles);
        const selectedProject = projects.find((p) => p.id === formData.project_id);
        await offlineAdvanceService.saveLocal({
          project_id: formData.project_id,
          project_name: selectedProject?.name,
          week_number: formData.week_number,
          year: formData.year,
          week_start: getWeekStart(),
          week_end: getWeekEnd(),
          description: formData.description || undefined,
          progress_percentage: formData.progress_percentage,
          contractor_specialty: formData.contractor_specialty || undefined,
          photos: photoDataUrls,
        });
        await refreshQueue();
        showInfo('Sin conexión: avance guardado localmente. Se sincronizará al volver la red.');
      }

      setFormData({
        project_id: initialProjectId ?? '',
        week_number: getCurrentWeekNumber(),
        year: new Date().getFullYear(),
        progress_percentage: 0,
        description: '',
        contractor_specialty: '',
      });
      setSelectedFiles([]);
      setPreviews([]);

      if (onSuccess) onSuccess();
      else navigate('/gestion/avances', { replace: true });
    } catch (err: any) {
      showError(err.message || 'Error al guardar avance');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    else navigate(-1);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-4 space-y-4">
      {!isOnline && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg px-3 py-2 flex items-start gap-2">
          <span>📴</span>
          <span>
            Sin conexión. El reporte se guardará localmente y se sincronizará automáticamente al
            recuperar señal.
          </span>
        </div>
      )}

      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-1">Proyecto *</label>
        <select
          required
          value={formData.project_id}
          onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
          className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
        >
          <option value="">Selecciona…</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">Semana</label>
          <input
            type="number"
            min={1}
            max={53}
            required
            value={formData.week_number}
            onChange={(e) =>
              setFormData({ ...formData, week_number: parseInt(e.target.value) || 1 })
            }
            className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1">Año</label>
          <input
            type="number"
            required
            value={formData.year}
            onChange={(e) =>
              setFormData({ ...formData, year: parseInt(e.target.value) || 2026 })
            }
            className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-1">Avance (%)</label>
        <input
          type="number"
          min={0}
          max={100}
          required
          value={formData.progress_percentage}
          onChange={(e) =>
            setFormData({ ...formData, progress_percentage: parseInt(e.target.value) || 0 })
          }
          className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-1">Especialidad</label>
        <input
          type="text"
          value={formData.contractor_specialty}
          onChange={(e) => setFormData({ ...formData, contractor_specialty: e.target.value })}
          placeholder="Ej. Albañilería"
          className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-1">Descripción</label>
        <textarea
          rows={3}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Trabajos realizados esta semana…"
          className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm resize-none focus:border-primary focus:outline-none"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-1.5">
          Fotos de evidencia
        </label>
        <label className="block w-full py-3 rounded-lg border-2 border-dashed border-slate-300 text-center text-sm text-slate-500 active:bg-slate-50 cursor-pointer">
          <span className="text-xl block">📸</span>
          <span className="text-xs">Toca para tomar o subir fotos</span>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
        </label>

        {previews.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mt-2">
            {previews.map((src, i) => (
              <div
                key={i}
                className="relative aspect-square rounded-lg overflow-hidden bg-slate-100"
              >
                <img src={src} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-600 text-white text-xs flex items-center justify-center"
                  aria-label="Eliminar"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={handleCancel}
          className="flex-1 py-2.5 rounded-lg bg-slate-100 text-slate-700 font-semibold text-sm"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className="flex-1 py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
        >
          {isSaving ? 'Guardando…' : isOnline ? 'Guardar' : 'Guardar offline'}
        </button>
      </div>
    </form>
  );
}